import { Link } from 'react-router-dom'
import { useLandscape } from '../hooks/useLandscape'
import { formatDate } from '../lib/format'

// REQ-064-AC3. When a mailbox's credential no longer opens, the screens that live off it say so where
// the person is looking, instead of showing the last read's results as though they were today's. The
// Connect checklist already carries the warning in the sidebar; somebody reading their voice profile
// is not looking at the sidebar.
//
// The health and its reason come from the API (`useLandscape`), which derives them on every read and
// never stores them — so this banner and the checklist can never disagree about a connector.

/** The banner's words. No hooks and no data of its own, so the whole of it renders in a test. */
export function MailboxWarningNote({ reason, lastReadAt, action = true }: {
  reason: string | null
  lastReadAt: string | null
  action?: boolean
}) {
  return (
    <div className="refusal mailbox-warning" role="status">
      {/* the API's reason is already a whole sentence ending in the ask ("… — reconnect it"); only the fallback needs one */}
      <strong>{reason ?? 'Your mailbox connection no longer opens — reconnect it'}.</strong>
      <p>
        {lastReadAt
          ? `What you see below is from the last successful read on ${formatDate(lastReadAt)}, not from your mail as it stands now.`
          : 'What you see below is from the last read that worked, not from your mail as it stands now.'}
        {' '}Nothing new has come in since, and nothing will until the connection is made again.
      </p>
      {action && <p><Link className="button primary" to="/mailbox?manage=1">Reconnect my mailbox</Link></p>}
    </div>
  )
}

/**
 * The banner, where the mailbox a screen depends on has stopped opening. Renders nothing at all when
 * the connection is fine — a warning nobody can explain is worse than saying nothing.
 * @param lastReadAt when the last successful read finished, if the screen knows.
 * @param action offer the reconnect button (the mailbox screen has its own, so it passes false).
 */
export function MailboxWarning({ lastReadAt = null, action = true }: { lastReadAt?: string | null; action?: boolean }) {
  const landscape = useLandscape()
  const mailbox = landscape.data?.systems.find((s) => s.id === 'mailbox')
  if (!mailbox || mailbox.health !== 'warning') return null
  return <MailboxWarningNote reason={mailbox.healthReason} lastReadAt={lastReadAt} action={action} />
}
